import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { post, postFormData } from '../Services/http';
import { uniqueId } from 'lodash-es';

export interface ReturnFormReducer {
  returns: ReturnItemProps[];
  returnType?: string;
  comment?: string;
  errors: ReturnFormError[];
  status: Status;
}

export interface ReturnFormError {
  id?: string;
  key?: string;
  error: string;
  title: string;
}

interface MappedReturnForm {
  returnType: string;
  comment?: string;
  returnItems: MappedReturnItem[];
}

interface MappedReturnItem {
  uniqueId: string;
  orderNumber: string;
  articleNumber: string;
  batchNumber: string;
  quantity: number;
  errorCode: string;
  comment: string;
  images: ReturnItemImage[];
}

export interface ReturnItemProps {
  uniqueId: string;
  orderNumber: string;
  articleNumber: string;
  batchNumber: string;
  quantity: number;
  errorCode: string;
  comment: string;
  images: ReturnItemImage[];
  imageStatus: Status;
  validationErrors: ValidationErrorsState;
}

export interface ReturnItemImage {
  name: string;
  fileSize: string;
  systemId: string;
  path: string;
}

export const ReturnItemKeys = {
  uniqueId: 'uniqueId',
  orderNumber: 'orderNumber',
  articleNumber: 'articleNumber',
  batchNumber: 'batchNumber',
  quantity: 'quantity',
  errorCode: 'errorCode',
  comment: 'comment',
  images: 'images',
};

export interface ValidationErrorsState {
  [key: string]: string | undefined;
}

export enum Status {
  fulfilled,
  pending,
  rejected,
  idle,
}

const DEFAULT_STATE: ReturnFormReducer = {
  status: Status.idle,
  errors: [],
  returns: [
    {
      uniqueId: uniqueId(),
      orderNumber: '',
      articleNumber: '',
      batchNumber: '',
      quantity: 0,
      errorCode: '',
      comment: '',
      images: [],
      imageStatus: Status.idle,
      validationErrors: {},
    },
  ],
};

const mapReturnItem = (item: ReturnItemProps): MappedReturnItem => {
  return {
    uniqueId: item.uniqueId,
    orderNumber: item.orderNumber,
    articleNumber: item.articleNumber,
    batchNumber: item.batchNumber,
    quantity: item.quantity,
    errorCode: item.errorCode,
    comment: item.comment,
    images: item.images,
  };
};

const mapReturnForm = (form: ReturnFormReducer): MappedReturnForm => {
  const { returnType, comment, returns } = form;
  const mappedReturns = returns.map((item) => mapReturnItem(item));

  return {
    returnType: returnType,
    comment: comment,
    returnItems: mappedReturns,
  };
};

export const submitForm = createAsyncThunk(
  'returnform/submit',
  async (returnForm: ReturnFormReducer, thunkApi) => {
    if (returnForm) {
      try {
        const response = await post(
          '/api/mypagereturn/submit',
          mapReturnForm(returnForm)
        );

        return await response.json();
      } catch (error) {
        const data = await error.response.json();
        return thunkApi.rejectWithValue(data);
      }
    }
  }
);

export const submitImage = createAsyncThunk(
  'returnform/imagesubmit',
  async ({ form, id }: { form: FormData; id: string }, thunkApi) => {
    if (form) {
      try {
        const response = await postFormData(
          '/api/mypagereturn/image',
          form
        );
        const data = await response.json();
        return thunkApi.fulfillWithValue({ data: data, uniqueId: id });
      } catch (error) {
        const data = await error.response.json();
        return thunkApi.rejectWithValue({ data: data, uniqueId: id });
      }
    }
  }
);

const ReturnFormSlice = createSlice({
  name: 'returnForm',
  initialState: DEFAULT_STATE,
  reducers: {
    addReturnItem: (state) => {
      state.returns.push({
        uniqueId: uniqueId(),
        orderNumber: '',
        articleNumber: '',
        batchNumber: '',
        quantity: 0,
        errorCode: '',
        comment: '',
        images: [],
        imageStatus: Status.idle,
        validationErrors: {},
      });
    },
    removeReturnItem: (state, action: PayloadAction<{ id: string }>) => {
      const returns = state.returns;
      const id = action.payload.id;
      const index = returns.findIndex((r) => r.uniqueId === id);

      if (index > -1 && index < state.returns.length) {
        returns.splice(index, 1);
      }
    },
    updateReturnItem: (
      state,
      action: PayloadAction<{
        key: string;
        value: any;
        id: string;
      }>
    ) => {
      const returns = state.returns;
      const { id, key, value } = action.payload;

      const index = returns.findIndex((r) => r.uniqueId === id);

      if (
        index > -1 &&
        index < state.returns.length &&
        typeof state.returns[index] !== undefined
      ) {
        state.returns[index][key] = value;
      }
    },
    clearErrors: (state) => {
      state.errors = [];
    },
    clearState: (state) => {
      state.status = Status.idle;
      state.errors = [];

      state.returns.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (typeof item[key] === 'object' && item[key] !== null) {
            item[key] = Array.isArray(item[key]) ? [] : {};
          } else {
            item[key] = DEFAULT_STATE.returns[0][key];
          }
        });
      });
    },
    removeReturnItemImage: (
      state,
      action: PayloadAction<{
        systemId: string;
        id: string;
      }>
    ) => {
      const { id, systemId } = action.payload;
      const returns = state.returns;
      const index = returns.findIndex((r) => r.uniqueId === id);

      if (
        index > -1 &&
        index < state.returns.length &&
        typeof state.returns[index] !== undefined
      ) {
        const imageIndex = state.returns[index].images.findIndex(
          (i) => i.systemId == systemId
        );

        if (imageIndex > -1) {
          state.returns[index]?.images.splice(imageIndex, 1);
        }
      }
    },
    setReturnType: (
      state,
      action: PayloadAction<{
        value: string;
      }>
    ) => {
      const value = action.payload.value;
      state.returnType = value;
    },
    setReturnComment: (
      state,
      action: PayloadAction<{
        value: string;
      }>
    ) => {
      const value = action.payload.value;
      state.comment = value;
    },
    setReturnStatus: (
      state,
      action: PayloadAction<{
        status: Status;
      }>
    ) => {
      const status = action.payload.status;
      state.status = status;
    },

    setReturnError: (
      state,
      action: PayloadAction<{
        error: ReturnFormError;
      }>
    ) => {
      const error = action.payload.error;

      state.errors.push(error);
    },
    setRowValidationErrors: (
      state,
      action: PayloadAction<{
        id: string;
        key: string;
        error?: string;
      }>
    ) => {
      const returns = state.returns;
      const { id, key, error } = action.payload;

      const index = returns.findIndex((r) => r.uniqueId === id);

      if (index > -1) {
        state.returns[index].validationErrors[key] = error;
      }
    },
    setImageStatus: (
      state,
      action: PayloadAction<{
        status: Status;
        id: string;
      }>
    ) => {
      const { id, status } = action.payload;
      const returns = state.returns;
      const index = returns.findIndex((r) => r.uniqueId === id);

      if (
        index > -1 &&
        index < state.returns.length &&
        typeof state.returns[index] !== undefined
      ) {
        state.returns[index].imageStatus = status;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submitImage.fulfilled, (state, action) => {
      const id = action.payload?.uniqueId;
      const images = action.payload?.data;

      const index = state.returns.findIndex((r) => r.uniqueId === id);

      if (index > -1 && index < state.returns.length && images) {
        images.map((image) => {
          state.returns[index].images.push(image);
          state.returns[index].imageStatus = Status.fulfilled;
        });
      }
    }),
      builder.addCase(submitImage.pending, (state, { meta }) => {
        const id = meta.arg.id;
        const returns = state.returns;

        const index = returns.findIndex((r) => r.uniqueId === id);
        if (index > -1) {
          state.returns[index].imageStatus = Status.pending;
        }
      }),
      builder.addCase(submitForm.pending, (state, action) => {
        state.status = Status.pending;
      });
    builder.addCase(submitForm.rejected, (state, action) => {
      const response = action.payload as ReturnFormError;
      const returns = state.returns;
      const { key, id, title, error } = response;
      state.status = Status.rejected;

      if (key) {
        const index = returns.findIndex((r) => r.uniqueId === id);

        if (index > -1) {
          state.returns[index].validationErrors[key] = error;

          state.errors.push(response);
        }
      } else {
        state.errors?.push(response);
      }
    });
    builder.addCase(submitForm.fulfilled, (state, action) => {
      state.status = Status.fulfilled;
    });
  },
});

export const ReturnFormActions = ReturnFormSlice.actions;

export default ReturnFormSlice.reducer;
